import { RenderRoutes, LessonEditor } from '@components';
import CustomerSuccessDashboard from '@components/Admin/CSDashboard';
import { useAuthState } from '@contexts/AuthContext';
import ActivityReport from '@modules/Admin/ActivityReport';
import AdmissionResults from '@modules/Admin/AdmissionResults/AdmissionResults';
import AtRiskStudents from '@modules/Admin/AtRiskStudents/AtRiskStudents';
import AuditLog from '@modules/Admin/AuditLog/AuditLog';
import CaseTracker from '@modules/Admin/CaseTracker/CaseTracker';
import CoursePlanGenerator from '@modules/Admin/CoursePlanGenerator/CoursePlanGenerator';
import CoursePlanReviews from '@modules/Admin/CoursePlanReviews/CoursePlanReviews';
import CourseDetails from '@modules/Admin/Courses/CourseDetails';
import Courses from '@modules/Admin/Courses/Courses';
import EmailLog from '@modules/Admin/EmailLog/EmailLog';
import ExtraServices from '@modules/Admin/ExtraServices';
import Flags from '@modules/Admin/Flags/Flags';
import HubspotOnboarding from '@modules/Admin/HubspotOnboarding/HubspotOnboarding';
import KnowledgeBaseEditor from '@modules/Admin/KnowledgeBaseEditor';
import MeetingIssues from '@modules/Admin/MeetingIssues/MeetingIssues';
import MentorPerformance from '@modules/Admin/MentorPerformance/MentorPerformance';
import MentorsV3, { MentorPageSessionModal } from '@modules/Admin/MentorsV3';
import NPSDashboard from '@modules/Admin/NPSDashboard';
import Reporting from '@modules/Admin/Reporting';
import StrategistsHandover from '@modules/Admin/Strategists/StrategistsHandover';
import Students from '@modules/Admin/Students';
import SystemHealth from '@modules/Admin/SystemHealth/SystemHealth';
import UserJourney from '@modules/Admin/UserJourney/UserJourney';
import UsersV3 from '@modules/Admin/UsersV3';
import Contracts from '@modules/Admin/pages/Contracts/Contracts';
import KnowledgeBase from '@modules/Mentor/KnowledgeBase';
import { UserRole } from '@shared/enums';
import AllSessions from './pages/AllSessions/AllSessions';
import Enrollments from './pages/Enrollments/Enrollments';
import Exams from './pages/Exams/Exams';
import K12Schools from './pages/K12/K12Schools';
import Majors from './pages/Majors/Majors';
import ParentSessionFeedback from './pages/ParentSessionFeedback/ParentSessionFeedback';
import ParentsSurvey from './pages/ParentsSurvey/ParentsSurvey';
import Profiles from './pages/Profiles/Profiles';
import Universities from './pages/Universities/Universities';

const Admin = () => {
  const { roles } = useAuthState();

  const excludeDefaultPages = roles.includes(UserRole.BDR);

  const routes = [];

  if (!excludeDefaultPages) {
    routes.push(
      { path: '/meeting-management', component: AllSessions },
      { path: '/manage-courses/:id', component: CourseDetails },
      { path: '/manage-courses', component: Courses },
      { path: '/course-plans', component: CoursePlanReviews },
      { path: '/lesson', component: LessonEditor },
      { path: '/flags', component: Flags },
      { path: '/majors', component: Majors },
      { path: '/enrollments', component: Enrollments },
      { path: '/exams', component: Exams },
      { path: '/manage-knowledge-base', component: KnowledgeBase },
      { path: '/knowledge-base/:id', component: KnowledgeBaseEditor },
      { path: '/knowledge-base', component: KnowledgeBaseEditor },
      { path: '/profiles', component: Profiles }
    );
  }

  if (roles.includes(UserRole.Financial)) {
    routes.push(
      { path: '/activity-report', component: ActivityReport },
      { path: '/reporting', component: Reporting },
      { path: '/extra-services', component: ExtraServices }
    );
  }

  if (roles.includes(UserRole.CustomerSuccess)) {
    routes.push(
      {
        path: '/customer-success',
        component: CustomerSuccessDashboard,
      },
      {
        path: '/nps-dashboard',
        component: NPSDashboard,
      }
    );
  }

  if (roles.includes(UserRole.StudentsManager)) {
    routes.push({ path: '/students', component: Students });
    routes.push({ path: '/parents-survey', component: ParentsSurvey });
    routes.push({ path: '/parent-session-feedback', component: ParentSessionFeedback });
  }

  if (roles.includes(UserRole.MentorsManager) || roles.includes(UserRole.BDR)) {
    routes.push({ path: '/mentors-v3', component: MentorsV3 });
  }

  if (roles.includes(UserRole.Financial) || roles.includes(UserRole.SuperUser)) {
    routes.push({ path: '/mentor-performance', component: MentorPerformance });
  }

  if (roles.includes(UserRole.Financial) || roles.includes(UserRole.SuperUser)) {
    routes.push({ path: '/at-risk-students', component: AtRiskStudents });
    routes.push({ path: '/case-tracker', component: CaseTracker });
    routes.push({ path: '/contracts', component: Contracts });
  }

  routes.push({ path: '/admission-results', component: AdmissionResults });

  if (roles.includes(UserRole.SuperUser)) {
    routes.push({ path: '/email-log', component: EmailLog });
    routes.push({ path: '/system-health', component: SystemHealth });
    routes.push({ path: '/meeting-issues', component: MeetingIssues });
    routes.push({ path: '/audit-log', component: AuditLog });
    routes.push({ path: '/user-journey', component: UserJourney });
    routes.push({ path: '/course-plan-generator', component: CoursePlanGenerator });
    routes.push({ path: '/strategists', component: StrategistsHandover });
    routes.push({ path: '/hubspot-onboarding', component: HubspotOnboarding });
  }

  if (roles.includes(UserRole.UsersManager)) {
    routes.push(
      { path: '/manage-users-v3', component: UsersV3 },
      { path: '/universities', component: Universities },
      { path: '/k12-schools', component: K12Schools }
    );
  }

  const redirectPath = roles.includes(UserRole.BDR) ? '/mentors-v3' : '/manage-users-v3';

  return <RenderRoutes routes={routes} redirectPath={redirectPath} />;
};

export default Admin;

export { MentorPageSessionModal };
